/**
 * Robust helpers for extracting user-facing error messages from API errors.
 *
 * FastAPI returns validation errors (422) as `detail: Array<{loc, msg, type}>`.
 * Passing this array directly to React (e.g. via a toast message prop) crashes
 * the render ("Objects are not valid as a React child"). Always pipe error
 * details through this helper before displaying them.
 */

interface ApiValidationError {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
}

interface AxiosLikeError {
  response?: {
    data?: {
      detail?: string | ApiValidationError[] | Record<string, unknown>;
    };
  };
  message?: string;
}

/**
 * Extract a user-friendly string message from an unknown API error.
 *
 * Handles:
 *  - String `detail` (standard FastAPI HTTPException)
 *  - Array `detail` (FastAPI 422 validation errors)
 *  - Axios error with `.message` (network errors, etc.)
 *  - Anything else → `fallback`
 */
export function extractApiErrorMessage(
  err: unknown,
  fallback = 'An error occurred'
): string {
  if (!err || typeof err !== 'object') return fallback;

  const axiosErr = err as AxiosLikeError;
  const raw = axiosErr.response?.data?.detail;

  if (typeof raw === 'string' && raw.trim()) {
    return raw;
  }

  if (Array.isArray(raw)) {
    const parts = raw
      .map((e) => {
        const item = e as ApiValidationError;
        const field = item.loc?.slice(1).join('.') || 'field';
        const msg = item.msg || 'Invalid value';
        return `${field}: ${msg}`;
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join('; ');
  }

  if (axiosErr.message && typeof axiosErr.message === 'string') {
    return axiosErr.message;
  }

  return fallback;
}
